/**
 * ============================================================
 * Publications.js — Public Publications Directory Page
 * ============================================================
 *
 * Loads:
 *   -> All publications (GET /api/publications)
 * Groups:
 *   -> Publications by year (DESC)
 *   -> “Show more” expands older years
 *
 * Backend returns each publication:
 *   {
 *     id, title, year, pubtype_id, pubtype,
 *     citation, abstract, pdf, doiurl
 *   }
 **/

import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";

import SynlabNavbar from "../components/SynlabNavbar";
import SynlabFooter from "../components/SynlabFooter";
import PubCard from "../components/PubCard";

import "./css/Page.css";     // global layout
import "./css/Subpage.css";  // shared layout for subpages


// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function Publications() {
  const [publications, setPublications] = useState([]);
  const [years, setYears] = useState([]);

  const [showMoreYears, setShowMoreYears] = useState(false);
  const YEAR_LIMIT = 3;

  // ----------------------------------------------------------
  // Load all publications
  // ----------------------------------------------------------
  useEffect(() => {
    async function load() {
      const data = await fetch("/api/publications").then(r => r.json());

      // Collect unique years from publications
      const yearList = [...new Set(data.map(p => p.year))]
        .filter(y => y && y !== 0)
        .sort((a, b) => b - a);   // newest -> oldest

      setPublications(data);
      setYears(yearList);
    }

    load();
  }, []);

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <div className="page-wrapper">

      {/* ----- NAVBAR ----- */}
      <header className="header">
        <SynlabNavbar />
      </header>

      {/* ----- FLEX COLUMN WRAPPER ----- */}
      <div className="inner-page-wrapper">

        {/* ----- HEADER IMAGE + TITLE OVERLAY ----- */}
        <div className="header-image-container">
          <img
            src="/headers/Header_pubs.png"
            alt="Publications Header"
            className="header-image"
          />
          <h1 className="header-title">Publications</h1>
        </div>

        {/* ----- MAIN CONTENT ----- */}
        <section className="page-content">
          <Container className="page-inner" fluid="lg">
            
            {/* ----- YEAR GROUPS ----- */}
            {years.map((year, index) => {
              if (!showMoreYears && index >= YEAR_LIMIT) return null;

              return (
                <div key={year}>
                  <h2 className="page-header">{year}</h2>

                  {publications
                    .filter(publication => publication.year === year)
                    .sort((a, b) =>
                      (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
                    )
                    .map(publication => (
                      <PubCard
                        key={publication.id}
                        publication={publication}
                      />
                    ))
                  }

                </div>
              );
            })}

            {/* ----- SHOW MORE BUTTON ----- */}
            {years.length > YEAR_LIMIT && (
              <button
                className="show-more-button"
                onClick={() => setShowMoreYears(!showMoreYears)}
              >
                {showMoreYears ? "Show Less" : "Show More"}
              </button>
            )}

          </Container>
        </section>

      </div>

      {/* ----- FOOTER ----- */}
      <SynlabFooter />
    </div>
  );
}

export default Publications;
